import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Divider,
  Grid2,
} from "@mui/material";
import {
  getDropdownCity,
  getDropdownState,
  getDropdownZipCode,
  getStorageValue,
  setStorageValue,
} from "../../utils/helper";
import PackageListings from "../../utils/services/PackageListing.api";
import AlertMessage from "../common/AlertMessage";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationPopup from "../../components/common/ConfirmationPopup.jsx";
import "../../styles/AdminProfilePage.css";
import Loaders from "../common/Loaders.jsx";
import SuccessDialog from "../common/SuccessDialog.jsx";

const EditProfile = () => {
  const navigate = useNavigate();
  const [profileInfo, setProfileInfo] = useState({});
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [zipcodeSearchTerm, setZipcodeSearchTerm] = useState("");
  const [profile, setProfile] = useState({
    // fullName: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: getStorageValue("userEmail") || "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
    },
  });

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [successModel, setSuccessModel] = useState(false);
  const [alertMessage, setAlertMessage] = useState({
    message: "",
    severity: "",
  });
  const [errors, setErrors] = useState({});
  const [cancelModal, setCancelModal] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  const handleCancel = () => {
    setCancelModal(false);
  };
  const handleopenCancelModal = () => {
    setCancelModal(true);
  };

  const handleCloseConfirm = () => {
    navigate("/profile");
  };

  const handleStateSearchChange = (event) => {
    setStateSearchTerm(
      event.target?.value?.replace(/\s\s+/g, " ")?.replace(/[^a-zA-Z ]+/gi, "")
    );
  };

  const filteredStates = states?.filter(
    (state) =>
      state.state.toLowerCase().startsWith(stateSearchTerm.toLowerCase()) ||
      state.stateCode.toLowerCase().startsWith(stateSearchTerm.toLowerCase())
  );

  const handleCitySearchChange = (event) => {
    setCitySearchTerm(
      event.target?.value?.replace(/\s\s+/g, " ")?.replace(/[^a-zA-Z ]+/gi, "")
    );
  };

  const handleZipcodeSearchChange = (event) => {
    setZipcodeSearchTerm(
      event.target?.value?.replace(/\s\s+/g, " ")?.replace(/[^0-9]+/gi, "")
    );
  };

  const filteredZipcodes = zipCodes?.filter((zipValue) =>
    zipValue?.toLowerCase().startsWith(zipcodeSearchTerm?.toLowerCase())
  );

  const filteredCities = cities?.filter((city) =>
    city?.toLowerCase().startsWith(citySearchTerm?.toLowerCase())
  );

  const validateFields = () => {
    const newErrors = {};

    const nameRegex = /^[a-zA-Z\s]/;

    if (
      !profile.firstName ||
      profile.firstName.length < 3 ||
      profile.firstName.length > 50 ||
      !nameRegex.test(profile.firstName)
    ) {
      newErrors.firstName =
        "First name must be between 3 and 50 alphabetic characters.";
    }

    if (
      !profile.lastName ||
      profile.lastName.length < 3 ||
      profile.lastName.length > 50 ||
      !nameRegex.test(profile.lastName)
    ) {
      newErrors.lastName =
        "Last name must be between 3 and 50 alphabetic characters.";
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(profile.phoneNumber)) {
      newErrors.phoneNumber = "Enter a valid 10-digit phone number.";
    }

    let valid = /^[a-zA-Z0-9\s\-’,_.#&;]+$/;
    if (
      !profile.address.street ||
      profile.address.street.length < 2 ||
      profile.address.street.length > 100 ||
      !valid.test(profile.address.street)
    ) {
      newErrors.street = "Address must be between 2 and 100 characters.";
    }

    if (!profile.address.state) {
      newErrors.state = "State is required.";
    }

    if (!profile.address.city) {
      newErrors.city = "City is required.";
    }

    if (!profile.address.zipCode) {
      newErrors.zipCode = "Zip Code is required.";
    }

    if (!profile.email) {
      newErrors.email = "Email is required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const getProfileDetails = async () => {
    const userId = getStorageValue("userId");
    setLoading(true);
    try {
      const res = await PackageListings.getUserProfile(userId);
      if (res.status === "success") {
        setProfileInfo(res.data);
      }
    } catch (err) {
      setAlertMessage({
        message:
          profileInfo?.roleId === 2
            ? "User Profile Details Update Failed"
            : "Admin Profile Details Update Failed",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProfileDetails();
    fetchStates();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (profileInfo && Object.keys(profileInfo).length > 0) {
      setProfile({
        firstName: profileInfo?.firstName || "",
        lastName: profileInfo?.lastName || "",
        phoneNumber: profileInfo?.phoneNumber || "",
        email: profileInfo?.email || "",
        address: {
          street: profileInfo?.address?.street || "",
          city: profileInfo?.address?.city || "",
          state: profileInfo?.address?.state || "",
          zipCode: profileInfo?.address?.zipCode || "",
        },
      });
      if (profileInfo?.address?.state) fetchCities(profileInfo?.address?.state);
      if (profileInfo?.address?.state && profileInfo?.address?.city) {
        fetchZipCodes(profileInfo?.address?.state, profileInfo?.address?.city);
      }
    }
    // eslint-disable-next-line
  }, [profileInfo]);

  const fetchStates = async () => {
    const response = await getDropdownState();
    setStates(response.data?.state || []);
  };

  const fetchCities = async (state) => {
    const response = await getDropdownCity(state);
    const cities = response.data?.city || [];
    setCities(cities);
    if (cities.length === 1) {
      const city = cities[0];
      setProfile((prev) => ({
        ...prev,
        address: { ...prev.address, city },
      }));
      fetchZipCodes(state, city);
    }
  };

  const fetchZipCodes = async (state, city) => {
    const response = await getDropdownZipCode(state, city);
    const zipCodes = response.data?.zipCode || [];
    setZipCodes(zipCodes);
    if (zipCodes.length === 1) {
      const zipCode = zipCodes[0];
      setProfile((prev) => ({
        ...prev,
        address: { ...prev.address, zipCode },
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("address.")) {
      const field = name.split(".")[1];
      const sanitizedValue = value?.replace(/[^a-zA-Z0-9\s\-’,_.#&;]/g, "");
      setProfile((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: sanitizedValue?.slice(0, 100) },
      }));
      if (field === "state") {
        fetchCities(value);
        setProfile((prev) => ({
          ...prev,
          address: { ...prev.address, city: "", zipCode: "" },
        }));
        setZipCodes([]);
      }
      if (field === "city") {
        fetchZipCodes(profile.address.state, value);
        setProfile((prev) => ({
          ...prev,
          address: { ...prev.address, zipCode: "" },
        }));
      }
    } else {
      if (name === "firstName") {
        const sanitizedValue = value?.replace(/[^a-zA-Z ’]/g, "");
        setProfile((prev) => ({
          ...prev,
          [name]: sanitizedValue?.slice(0, 50),
        }));
      } else if (name === "lastName") {
        const sanitizedValue = value?.replace(/[^a-zA-Z ’]/g, "");
        setProfile((prev) => ({
          ...prev,
          [name]: sanitizedValue?.slice(0, 50),
        }));
      } else if (name === "phoneNumber") {
        const sanitizedValue = value?.replace(/[^0-9]/g, "");
        setProfile((prev) => ({
          ...prev,
          [name]: sanitizedValue.slice(0, 10),
        }));
      } else if (name === "email") {
        setProfile((prev) => ({ ...prev, email: value }));
      } else {
        setProfile((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleSave = async () => {
    if (!validateFields()) {
      return;
    }
    setLoading(true);

    const payload = {
      id: profileInfo?.id || 0,
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email,
      countryCode: "+1",
      phoneNumber: profile.phoneNumber,
      address: {
        street: profile.address.street,
        city: profile.address.city,
        state: profile.address.state,
        zipCode: profile.address.zipCode,
      },
    };

    try {
      await PackageListings.editUserProfile(payload);
      setAlertMessage({
        message:
          profileInfo?.roleId === 2
            ? "User profile updated successfully"
            : "Admin profile updated successfully",
        severity: "success",
      });
      setStorageValue("updatePhase", "Updated");
      setStorageValue("userEmail", profile.email);
      setSuccessModel(true);
      setIsUpdated(true);
    } catch (error) {
      if (
        error?.response?.data?.message ===
        "User with the same email or phone number already exists."
      ) {
        setAlertMessage({
          message:
            profileInfo?.roleId === 2
              ? "User Profile Update Failed" + error?.response?.data?.message
              : "Admin Profile Update Failed: " +
                error?.response?.data?.message,
          severity: "error",
        });
      } else {
        setAlertMessage({
          message:
            profileInfo?.roleId === 2
              ? "User Profile Update Failed"
              : "Admin Profile Update Failed",
          severity: "error",
        });
      }
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSuccessClose = () => {
    setSuccessModel(false);
    setStorageValue("updatePhase", "");
    navigate("/profile");
  };

  const inputStyles = {
    height: "40px",
    fontSize: "13px",
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "#000000",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "14px",
      "& fieldset": {
        borderColor: "#D0D5DD",
      },
      "&:hover fieldset": {
        borderColor: "#D0D5DD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#D0D5DD",
      },
    },
  };

  return (
    <Box sx={{ minHeight: "100vh", padding: 2 }}>
      <Button
        sx={{
          color: "#2579CD",
          textTransform: "none",
          px: "0",
          "&:disabled": {
            color: "#2579CD",
          },
        }}
        variant="none"
        size="medium"
        startIcon={<ArrowBackIcon />}
        onClick={handleopenCancelModal}
        disabled={isUpdated}
      >
        Back
      </Button>

      <>
        <Typography sx={{ fontWeight: "500", fontSize: "24px", mb: 2 }}>
          {profileInfo?.roleId === 1 ? "Edit Admin Profile" : "Edit Profile"}
        </Typography>
        {loading && <Loaders />}

        <Grid2 container sx={{ color: "#344054" }} spacing={2}>
          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                fontFamily: "Poppins!important",
              }}
            >
              First Name
              <Typography
                component="span"
                sx={{ color: "red", fontWeight: "bold" }}
              >
                *
              </Typography>
            </Typography>
            <TextField
              fullWidth
              name="firstName"
              placeholder="Enter First Name"
              value={profile.firstName}
              slotProps={{htmlInput: {maxLength: 50}}}
              error={!!errors.firstName}
              onChange={handleChange}
              variant="outlined"
              size="small"
              sx={inputStyles}
            />
            <Typography variant="caption" color="error">
              {errors.firstName}
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                fontFamily: "Poppins!important",
              }}
            >
              Last Name
              <Typography
                component="span"
                sx={{ color: "red", fontWeight: "bold" }}
              >
                *
              </Typography>
            </Typography>
            <TextField
              fullWidth
              name="lastName"
              placeholder="Enter Last Name"
              value={profile.lastName}
              slotProps={{htmlInput: {maxLength: 50}}}
              error={!!errors.lastName}
              onChange={handleChange}
              variant="outlined"
              size="small"
              sx={inputStyles}
            />
            <Typography variant="caption" color="error">
              {errors.lastName}
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                fontFamily: "Poppins!important",
              }}
            >
              Phone number
              <Typography
                component="span"
                sx={{ marginLeft: "5px", color: "red", fontWeight: "bold" }}
              >
                *
              </Typography>
            </Typography>
            <TextField
              fullWidth
              type="text"
              name="phoneNumber"
              placeholder="Enter Phone Number"
              value={profile.phoneNumber}
              onChange={handleChange}
              error={!!errors.phoneNumber}
              variant="outlined"
              size="small"
              sx={{
                height: "40px",
                fontSize: "13px",
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000000",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: profileInfo?.roleId === 2 && "#F3F4F4",
                  "& fieldset": {
                    borderColor: "#D0D5DD",
                  },
                  "&:hover fieldset": {
                    borderColor: "#D0D5DD",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#D0D5DD",
                  },
                },
              }}
              disabled={profileInfo?.roleId === 2}
              slotProps={{
                htmlInput: {maxLength: 10},
                input: {
                  startAdornment: (
                    <>
                      <InputAdornment position="start"><span style={{color: "black"}}>+1 </span></InputAdornment>
                      <Divider
                        sx={{ height: 28, m: 0.5 }}
                        orientation="vertical"
                      />
                    </>
                  ),
                },
              }}
            />
            <Typography variant="caption" color="error">
              {errors.phoneNumber}
            </Typography>
          </Grid2>

          {profileInfo?.roleId === 2 && (
            <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  fontFamily: "Poppins!important",
                }}
              >
                Email
                <Typography
                  component="span"
                  sx={{ marginLeft: "5px", color: "red", fontWeight: "bold" }}
                >
                  *
                </Typography>
              </Typography>
              <TextField
                fullWidth
                type="text"
                name="email"
                placeholder="Enter Email"
                value={profile.email}
                onChange={handleChange}
                slotProps={{ htmlInput: {maxLength: 55}}}
                error={!!errors.email}
                variant="outlined"
                size="small"
                sx={inputStyles}
                disabled={profileInfo?.roleId === 1}
              />
              <Typography variant="caption" color="error">
                {errors.email}
              </Typography>
            </Grid2>
          )}

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                fontFamily: "Poppins!important",
              }}
            >
              {" "}
              Address{" "}
              <Typography
                component="span"
                sx={{ color: "red", fontWeight: "bold" }}
              >
                *
              </Typography>
            </Typography>
            <TextField
              fullWidth
              name="address.street"
              placeholder="Enter Address here"
              value={profile.address.street}
              slotProps={{ htmlInput: {maxLength: 100}}}
              onChange={handleChange}
              error={!!errors.street}
              variant="outlined"
              size="small"
              sx={inputStyles}
            />
            <Typography variant="caption" color="error">
              {errors.street}
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
            <Typography
              sx={{
                fontFamily: "Poppins!important",
              }}
              className="form-label required"
            >
              State
            </Typography>
            <FormControl fullWidth>
              <Select
                name="address.state"
                value={profile.address.state}
                onChange={handleChange}
                displayEmpty
                size="small"
                sx={inputStyles}
                onOpen={() => {
                  setStateSearchTerm("");
                }}
                MenuProps={{ autoFocus: false }}
                renderValue={(selected) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#A6ADB7" }}>Select state</span>
                  )
                }
              >
                <MenuItem
                  className="dropdown-search"
                  disableRipple
                  sx={{ background: "transparent" }}
                  value={profile.address.state}
                >
                  <TextField
                    placeholder="Search state"
                    size="small"
                    sx={{ 
                      outline: "none", 
                      border: "none",
                      "& fieldset": { border: 'none' },
                    }}
                    fullWidth
                    value={stateSearchTerm}
                    slotProps={{htmlInput: {maxLength: 30}}}
                    onChange={handleStateSearchChange}
                    autoFocus
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                      if (event.key !== "Escape") {
                        event.stopPropagation();
                      }
                    }}
                  />
                </MenuItem>
                {filteredStates?.length > 0 ? (
                  filteredStates?.map((state) => (
                    <MenuItem key={state.state} value={state.state}>
                      {state.state}
                    </MenuItem>
                  ))
                ) : filteredStates?.length === 0 && profile.address.state ? (
                  <MenuItem value={profile.address.state}>
                    No results found
                  </MenuItem>
                ) : (
                  <MenuItem disabled>No results found</MenuItem>
                )}
              </Select>
              <Typography variant="caption" color="error">
                {errors.state}
              </Typography>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
            <Typography
              sx={{
                fontFamily: "Poppins!important",
              }}
              className="form-label required"
            >
              City
            </Typography>
            <FormControl fullWidth>
              <Select
                name="address.city"
                value={profile.address.city}
                onChange={handleChange}
                displayEmpty
                onOpen={() => {
                  setCitySearchTerm("");
                }}
                disabled={!profile.address.state}
                size="small"
                sx={inputStyles}
                renderValue={(selected) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#A6ADB7" }}>Select city</span>
                  )
                }
                MenuProps={{ autoFocus: false }}
              >
                {profile.address.state && (
                  <MenuItem
                    className="dropdown-search"
                    disableRipple
                    sx={{ background: "transparent" }}
                    value={profile.address.city}
                  >
                    <TextField
                      placeholder="Search City"
                      size="small"
                      sx={{ 
                        outline: "none", 
                        border: "none",
                        "& fieldset": { border: 'none' },
                      }}
                      fullWidth
                      value={citySearchTerm}
                      slotProps={{htmlInput: {maxLength: 30}}}
                      onChange={handleCitySearchChange}
                      autoFocus
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key !== "Escape") {
                          event.stopPropagation();
                        }
                      }}
                    />
                  </MenuItem>
                )}
                {filteredCities?.length > 0 && profile.address.state ? (
                  filteredCities?.map((city) => (
                    <MenuItem key={city} value={city ?? ""}>
                      {city}
                    </MenuItem>
                  ))
                ) : filteredCities?.length === 0 && profile.address.city ? (
                  <MenuItem value={profile.address.city}>
                    No results found
                  </MenuItem>
                ) : (
                  <MenuItem disabled>No results found</MenuItem>
                )}
              </Select>
              <Typography variant="caption" color="error">
                {errors.city}
              </Typography>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
            <Typography
              sx={{
                fontFamily: "Poppins!important",
              }}
              className="form-label required"
            >
              Zipcode
            </Typography>
            <FormControl fullWidth>
              <Select
                name="address.zipCode"
                value={profile.address.zipCode}
                onChange={handleChange}
                displayEmpty
                disabled={!profile.address.city}
                size="small"
                sx={inputStyles}
                onOpen={() => {
                  setZipcodeSearchTerm("");
                }}
                MenuProps={{ autoFocus: false }}
                renderValue={(selected) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#A6ADB7" }}>Select zipcode</span>
                  )
                }
              >
                {
                  <MenuItem
                    className="dropdown-search"
                    disableRipple
                    sx={{ background: "transparent" }}
                    value={profile.address.zipCode}
                  >
                    <TextField
                      placeholder="Search Zipcode"
                      size="small"
                      sx={{ 
                        outline: "none", 
                        border: "none",
                        "& fieldset": { border: 'none' },
                      }}
                      fullWidth
                      value={zipcodeSearchTerm}
                      slotProps={{htmlInput: {maxLength: 30}}}
                      onChange={handleZipcodeSearchChange}
                      autoFocus
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key !== "Escape") {
                          event.stopPropagation();
                        }
                      }}
                    />
                  </MenuItem>
                }
                {filteredZipcodes?.length > 0 ? (
                  filteredZipcodes?.map((zipCode) => (
                    <MenuItem key={zipCode} value={zipCode}>
                      {zipCode}
                    </MenuItem>
                  ))
                ) : filteredZipcodes?.length === 0 &&
                  profile.address.zipCode ? (
                  <MenuItem value={profile.address.zipCode}>
                    No results found
                  </MenuItem>
                ) : (
                  <MenuItem disabled>No results found</MenuItem>
                )}
              </Select>
              <Typography variant="caption" color="error">
                {errors.zipCode}
              </Typography>
            </FormControl>
          </Grid2>
        </Grid2>
        <ConfirmationPopup
          open={cancelModal}
          onClose={handleCancel}
          onConfirm={handleCloseConfirm}
          title="Cancel Confirmation"
          content="Are you sure you want to cancel? All unsaved changes will be lost."
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 13,
            gap: "12px",
          }}
        >
          {!isUpdated && (
            <>
              {" "}
              <Button
                variant="outlined"
                color="white"
                onClick={handleopenCancelModal}
                sx={{
                  textTransform: "none",
                  borderColor: "#D0D5DD",
                  width: "130px",
                  borderRadius: "8px",
                }}
              >
                Cancel
              </Button>
              <Button
                sx={{
                  textTransform: "none",
                  width: "130px",
                  borderRadius: "8px",
                  background: "#0C68F4",
                }}
                variant="contained"
                color="primary"
                onClick={handleSave}
              >
                Update
              </Button>
            </>
          )}
        </Box>
        <AlertMessage
          open={open}
          handleClose={handleClose}
          message={alertMessage.message}
          severity={alertMessage.severity}
        />
        <SuccessDialog 
          open={successModel}
          handleClose={handleSuccessClose}
          message={alertMessage.message}
        />
      </>
    </Box>
  );
};

export default EditProfile;
